import { immer } from "zustand/middleware/immer";
import { create } from "zustand";
import { createSelectors } from "@/lib/storeUtils";

export type QuestionsType = {
    active: number; // 0 or 1
    id: number;
    text: string;
};

export type LoginSipErrorResponse = {
    questions?: QuestionsType[];
    tfa: string;
    email: string;
    change_pwd: boolean;
    token: string | null;
    showSendMail: boolean;
    showCodeField: boolean;
    showSendEmail: boolean;
    availabletfa: string[];
    showCaptcha?: boolean;
};

export type LoginFlowState = {
    // Loading states
    loginLoading: boolean;
    registerLoading: boolean;
    forgotPasswordLoading: boolean;
    resetPasswordLoading: boolean;

    // TFA state
    requireOneTimePassword: boolean;
    tfaReason: string | null;
    tfa: string | null;
    availableTfa: string[];
    showSendMail: boolean;

    // Login flow state
    loginQuestions: QuestionsType[];
    requireCaptcha: boolean;
    activeSession: {
        showDialog: boolean;
        tempToken: string | null;
    };
    sipLoginResponse: LoginSipErrorResponse | null;

    // Login form values (for SSO flow persistence across remounts)
    loginFormValues: {
        email: string;
        password: string;
        social_token: string;
        provider: string;
    };
};

const baseLoginFlowStore = immer<LoginFlowState>(() => ({
    // Loading states
    loginLoading: false,
    registerLoading: false,
    forgotPasswordLoading: false,
    resetPasswordLoading: false,

    // TFA state
    requireOneTimePassword: false,
    tfaReason: null,
    tfa: null,
    availableTfa: [],
    showSendMail: false,

    // Login flow state
    loginQuestions: [],
    requireCaptcha: false,
    activeSession: {
        showDialog: false,
        tempToken: null,
    },
    sipLoginResponse: null,

    // Login form values
    loginFormValues: {
        email: "",
        password: "",
        social_token: "",
        provider: "",
    },
}));

export const loginFlowStore = create(baseLoginFlowStore);

export function setLoginLoading(loading: boolean): void {
    loginFlowStore.setState((state) => {
        state.loginLoading = loading;
    });
}

export function setRegisterLoading(loading: boolean): void {
    loginFlowStore.setState((state) => {
        state.registerLoading = loading;
    });
}

export function setForgotPasswordLoading(loading: boolean): void {
    loginFlowStore.setState((state) => {
        state.forgotPasswordLoading = loading;
    });
}

export function setResetPasswordLoading(loading: boolean): void {
    loginFlowStore.setState((state) => {
        state.resetPasswordLoading = loading;
    });
}

export function setTfaState(tfa: {
    requireOneTimePassword?: boolean;
    tfaReason?: string | null;
    tfa?: string | null;
    availableTfa?: string[];
    showSendMail?: boolean;
}): void {
    loginFlowStore.setState((state) => {
        if (tfa.requireOneTimePassword !== undefined) state.requireOneTimePassword = tfa.requireOneTimePassword;
        if (tfa.tfaReason !== undefined) state.tfaReason = tfa.tfaReason;
        if (tfa.tfa !== undefined) state.tfa = tfa.tfa;
        if (tfa.availableTfa !== undefined) state.availableTfa = tfa.availableTfa;
        if (tfa.showSendMail !== undefined) state.showSendMail = tfa.showSendMail;
    });
}

export function resetTfaState(): void {
    loginFlowStore.setState((state) => {
        state.requireOneTimePassword = false;
        state.tfaReason = null;
        state.tfa = null;
        state.availableTfa = [];
        state.showSendMail = false;
    });
}

export function setLoginFlowState(loginFlow: {
    loginQuestions?: QuestionsType[];
    requireCaptcha?: boolean;
    activeSession?: {
        showDialog?: boolean;
        tempToken?: string | null;
    };
    sipLoginResponse?: LoginSipErrorResponse | null;
}): void {
    loginFlowStore.setState((state) => {
        if (loginFlow.loginQuestions !== undefined) state.loginQuestions = loginFlow.loginQuestions;
        if (loginFlow.requireCaptcha !== undefined) state.requireCaptcha = loginFlow.requireCaptcha;
        if (loginFlow.activeSession !== undefined) {
            if (loginFlow.activeSession.showDialog !== undefined) {
                state.activeSession.showDialog = loginFlow.activeSession.showDialog;
            }
            if (loginFlow.activeSession.tempToken !== undefined) {
                state.activeSession.tempToken = loginFlow.activeSession.tempToken;
            }
        }
        if (loginFlow.sipLoginResponse !== undefined) state.sipLoginResponse = loginFlow.sipLoginResponse;
    });
}

export function setLoginFormValues(values: {
    email?: string;
    password?: string;
    social_token?: string;
    provider?: string;
}): void {
    loginFlowStore.setState((state) => {
        if (values.email !== undefined) state.loginFormValues.email = values.email;
        if (values.password !== undefined) state.loginFormValues.password = values.password;
        if (values.social_token !== undefined) state.loginFormValues.social_token = values.social_token;
        if (values.provider !== undefined) state.loginFormValues.provider = values.provider;
    });
}

export function resetLoginFlowState(): void {
    loginFlowStore.setState((state) => {
        state.loginQuestions = [];
        state.requireCaptcha = false;
        state.activeSession = {
            showDialog: false,
            tempToken: null,
        };
        state.sipLoginResponse = null;
        state.loginFormValues = {
            email: "",
            password: "",
            social_token: "",
            provider: "",
        };
    });
}

export function resetAllLoginState(): void {
    resetTfaState();
    resetLoginFlowState();
}

export const useLoginFlowStore = createSelectors(loginFlowStore);
